/**
 * Maps a FaceLock Dev session onto the banking demo applicant.
 *
 * Session status is pending | completed | expired | failed.
 * On a completed session, livenessVerified is livenessProven === true
 * and matchLevel >= 6 (the same threshold as the liveness Cloud Function),
 * regardless of disposition.
 * identityVerified is the human-review gate: true for accepted or passed,
 * false for rejected, and left unchanged for needs_review.
 * needs_review never auto-approves identity.
 */

export const LIVENESS_FIELD = "livenessVerified";
export const IDENTITY_FIELD = "identityVerified";
/** Boolean written by the existing liveness Cloud Function when matchLevel >= 6. */
export const PROVEN_LIVENESS_VALUE = true;
/** Same match threshold as the liveness Cloud Function. */
export const LIVENESS_MATCH_LEVEL_THRESHOLD = 6;

const SESSION_STATUSES = new Set(["pending", "completed", "expired", "failed", "in_progress"]);
const TERMINAL_STATUSES = new Set(["completed", "failed", "expired"]);
const DISPOSITIONS = new Set(["passed", "needs_review", "accepted", "rejected"]);

export type FacelockOutcome =
  | "accepted"
  | "rejected"
  | "failed"
  | "expired"
  | "needs_review"
  | "recorded";

export interface MappedFacelockEvent {
  eventType: string | null;
  referenceId: string | null;
  applicantId: string | null;
  disposition: string | null;
  /** Coarse session status: pending | completed | expired | failed, when known. */
  status: string | null;
  outcome: FacelockOutcome;
  /**
   * true  → write livenessVerified true
   * false → write livenessVerified false
   * null  → leave the existing field unchanged
   */
  setLivenessVerified: boolean | null;
  /**
   * true  → write identityVerified true
   * false → write identityVerified false
   * null  → leave the existing field unchanged (including needs_review)
   */
  setIdentityVerified: boolean | null;
  matchLevel: number | null;
  livenessProven: boolean | null;
  didMatchOcrTemplate: boolean | null;
  /** True when the payload has no idvDisposition and the session should be fetched. */
  needsDispositionLookup: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeToken(value: string | null): string | null {
  if (!value) return null;
  const token = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return token.length > 0 ? token : null;
}

function pickString(source: Record<string, unknown> | null, keys: string[]): string | null {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function nestedRecords(body: Record<string, unknown>): Record<string, unknown>[] {
  const records = [body];
  for (const key of ["data", "payload", "challenge", "session"]) {
    if (isRecord(body[key])) records.push(body[key] as Record<string, unknown>);
  }
  return records;
}

function callbackApplicantId(records: Record<string, unknown>[]): string | null {
  for (const record of records) {
    const direct = pickString(record, ["applicantId"]);
    if (direct) return direct;
    const raw = record.callbackData;
    if (isRecord(raw)) {
      const id = pickString(raw, ["applicantId"]);
      if (id) return id;
    }
  }
  return null;
}

function dispositionFrom(records: Record<string, unknown>[]): string | null {
  for (const record of records) {
    if (!Object.prototype.hasOwnProperty.call(record, "idvDisposition")) continue;
    const nested = record.idvDisposition;
    if (!isRecord(nested)) continue;
    const inner = normalizeToken(pickString(nested, ["disposition"]));
    if (inner && DISPOSITIONS.has(inner)) return inner;
  }
  return null;
}

function sessionStatusFrom(event: string | null, status: string | null): string | null {
  if (status === "in_progress" || status === "pending") return status;
  if (status && SESSION_STATUSES.has(status)) return status;
  if (event === "challenge.expired") return "expired";
  if (event === "challenge.failed") return "failed";
  if (
    event === "challenge.completed" ||
    event === "challenge.idv_completed" ||
    event === "challenge.disposition.updated"
  ) {
    return "completed";
  }
  return null;
}

function isTerminal(status: string | null): boolean {
  return !!status && TERMINAL_STATUSES.has(status);
}

function pickBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function pickMatchLevel(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  return value;
}

function verificationMetrics(records: Record<string, unknown>[]): {
  livenessProven: boolean | null;
  matchLevel: number | null;
  didMatchOcrTemplate: boolean | null;
} {
  for (const record of records) {
    const raw = record.idvVerificationMetrics;
    if (!isRecord(raw)) continue;
    return {
      livenessProven: pickBoolean(raw.livenessProven),
      matchLevel: pickMatchLevel(raw.matchLevel),
      didMatchOcrTemplate: pickBoolean(raw.didMatchOcrTemplate),
    };
  }
  return { livenessProven: null, matchLevel: null, didMatchOcrTemplate: null };
}

export function mapFacelockWebhook(body: unknown): MappedFacelockEvent {
  const root = isRecord(body) ? body : {};
  const records = nestedRecords(root);
  const eventType = pickString(root, ["event"]);
  const referenceId = records
    .map((record) => pickString(record, ["referenceId"]))
    .find((value) => !!value) ?? null;
  const applicantId = callbackApplicantId(records);
  const disposition = dispositionFrom(records);
  const statusRaw = normalizeToken(
    records.map((record) => pickString(record, ["status"])).find((value) => !!value) ?? null
  );
  const event = normalizeToken(eventType);
  const status = sessionStatusFrom(event, statusRaw);

  const metrics = verificationMetrics(records);
  const terminal = isTerminal(status);
  let outcome: FacelockOutcome = "recorded";
  let setIdentityVerified: boolean | null = null;
  let setLivenessVerified: boolean | null = null;

  if (status === "completed") {
    const proven =
      metrics.livenessProven === true &&
      metrics.matchLevel !== null &&
      metrics.matchLevel >= LIVENESS_MATCH_LEVEL_THRESHOLD;
    setLivenessVerified = proven ? PROVEN_LIVENESS_VALUE : false;
  }

  if (!terminal) {
    outcome = "recorded";
  } else if (disposition === "accepted" || disposition === "passed") {
    outcome = "accepted";
    setIdentityVerified = true;
  } else if (disposition === "rejected") {
    outcome = "rejected";
    setIdentityVerified = false;
  } else if (disposition === "needs_review") {
    outcome = "needs_review";
  } else if (event === "challenge.expired" || status === "expired") {
    outcome = "expired";
  } else if (event === "challenge.failed" || status === "failed") {
    outcome = "failed";
  }

  return {
    eventType,
    referenceId,
    applicantId,
    disposition: terminal ? disposition : null,
    status,
    outcome,
    setLivenessVerified,
    setIdentityVerified,
    matchLevel: metrics.matchLevel,
    livenessProven: metrics.livenessProven,
    didMatchOcrTemplate: metrics.didMatchOcrTemplate,
    needsDispositionLookup: terminal && disposition === null && !!referenceId,
  };
}

export function facelockStatusLabel(
  status: string | undefined | null,
  disposition?: string | null
): string | null {
  const sessionStatus = normalizeToken(status ?? null);
  if (sessionStatus === "pending" || sessionStatus === "in_progress") return "Link sent";
  const disp = normalizeToken(disposition ?? null);
  if (disp === "accepted" || disp === "passed") return "Accepted";
  if (disp === "rejected") return "Rejected";
  if (disp === "needs_review") return "Needs review";
  switch (normalizeToken(status ?? null)) {
    case "pending":
      return "Link sent";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "expired":
      return "Expired";
    case "recorded":
      return "Update received";
    case "accepted":
    case "approved":
      return "Accepted";
    default:
      return status ? status : null;
  }
}
